import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import UserAutocomplete from '../components/UserAutocomplete';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Berechnet die Default-Rundenzahl für ein Americano-Turnier, konsistent mit dem
 * Backend (americano_logic.compute_default_rounds) und TournamentCreate.jsx.
 */
function computeAmericanoActivePlayers(numTeams, numCourts) {
  const courts = Math.max(1, numCourts || 1);
  const cap = Math.min(numTeams, courts * 4);
  return Math.max(0, cap - (cap % 4));
}

function computeAmericanoDefaultRounds(numTeams, numCourts) {
  const active = computeAmericanoActivePlayers(numTeams, numCourts);
  if (active <= 0 || !numTeams) return 0;
  if (active >= numTeams) return Math.max(1, numTeams - 1);
  return Math.max(1, Math.ceil((numTeams * (numTeams - 1)) / active));
}


// Random Namen Generator
const FIRST_NAMES = ["Max", "Anna", "Leon", "Sophie", "Felix", "Emma", "Paul", "Mia", "Jonas", "Hannah", "Lukas", "Marie", "Ben", "Lina", "Tim", "Sarah", "Jan", "Laura", "Tom", "Lisa"];
const LAST_NAMES = ["Müller", "Schmidt", "Schneider", "Fischer", "Weber", "Meyer", "Wagner", "Becker", "Schulz", "Hoffmann", "Koch", "Richter", "Klein", "Wolf", "Schröder", "Neumann", "Braun", "Zimmermann"];

const generateRandomPlayerName = () => {
  const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
  const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
  return `${firstName} ${lastName}`;
};

function TournamentEdit() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name: '',
    date: '',
    description: '',
    num_teams: '',
    num_courts: '',
    num_groups: '4',
    tournament_type: 'ko',
    group_assignment_mode: 'auto',
    teams_advance_from_group: '2',
    is_active: 0,
    max_rounds: '5',
    ko_consolation_round: false,
    best_of_three: false,
    show_in_presentation: true,
    draw_generated: 0,
    start_time: '',
    match_duration: '20',
    break_duration: '5',
    landesliga_categories: '',
  });

  const [courtNames, setCourtNames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newClubName, setNewClubName] = useState('');
  const [newClubPairings, setNewClubPairings] = useState([]);
  const [clubError, setClubError] = useState('');
  const [clubSaving, setClubSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState(id || '');

  // Helper function to format date with leading zeros (DD.MM.YYYY)
  const formatDateWithLeadingZeros = (date) => {
    if (!date) return t('tournamentDelete.noDate');

    // Wenn Datum bereits im deutschen Format ist (DD.MM.YYYY), direkt zurückgeben
    if (typeof date === 'string' && date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      return date;
    }

    // Ansonsten von ISO-Format konvertieren
    const d = new Date(date);
    if (isNaN(d.getTime())) return t('tournamentDelete.invalidDate');

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    return `${day}.${month}.${year}`;
  };

  // Helper function to format tournament display
  const formatTournamentDisplay = (tournament) => {
    if (!tournament) return '';

    // Format date as DD.MM.YYYY with leading zeros
    const dateStr = formatDateWithLeadingZeros(tournament.date);

    // Get creator name
    const creatorName = [tournament.created_by_first_name, tournament.created_by_last_name]
      .filter(Boolean)
      .join(' ') || t('tournamentDelete.unknown');

    // Build display string
    let display = `${dateStr} - ${tournament.name} - ${creatorName}`;

    // Add green checkmark for active tournaments
    if (tournament.is_active === 1) {
      display += ' ✓';
    }

    return display;
  };

  // Load tournaments list
  useEffect(() => {
    const fetchTournaments = async () => {
        try {
        const response = await axios.get('/api/tournaments');

        // Sort tournaments by date (newest first)
        const sortedTournaments = [...response.data].sort((a, b) => {
          if (!a.date && !b.date) return 0;
          if (!a.date) return 1;
          if (!b.date) return -1;

          // Konvertiere deutsches Format (DD.MM.YYYY) zu Date-Objekt falls nötig
          const parseDate = (dateStr) => {
            if (typeof dateStr === 'string' && dateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
              const [day, month, year] = dateStr.split('.');
              return new Date(`${year}-${month}-${day}`);
            }
            return new Date(dateStr);
          };

          return parseDate(b.date) - parseDate(a.date);
        });

        setTournaments(sortedTournaments);
      } catch (err) {
        console.error('Fehler beim Laden der Turniere:', err);
        setError('Fehler beim Laden der Turnierliste');
      }
    };

    fetchTournaments();
  }, []);


  // Load tournament data when selectedTournamentId changes
  useEffect(() => {
    const fetchTournamentData = async () => {
      if (!selectedTournamentId) return;

      try {
        setLoading(true);
        const [tournamentRes, teamsRes] = await Promise.all([
          axios.get(`/api/tournaments/${selectedTournamentId}`),
          axios.get(`/api/tournaments/${selectedTournamentId}/teams`)
        ]);

        const tournamentData = tournamentRes.data;

        // Parse court names
        const courtNamesArray = tournamentData.court_names
          ? tournamentData.court_names.split(',').map(name => name.trim())
          : [];

        // Konvertiere Datum vom deutschen Format (DD.MM.YYYY) zu ISO (YYYY-MM-DD)
        let isoDate = '';
        if (tournamentData.date) {
          // Prüfe ob Datum bereits im ISO-Format ist (YYYY-MM-DD)
          if (tournamentData.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            isoDate = tournamentData.date;
          }
          // Prüfe ob Datum im deutschen Format ist (DD.MM.YYYY)
          else if (tournamentData.date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
            const [day, month, year] = tournamentData.date.split('.');
            isoDate = `${year}-${month}-${day}`;
          }
        }

        // Set form data
        console.log('Loading tournament data:', tournamentData);
        console.log('Time fields from backend - start_time:', tournamentData.start_time, 'match_duration:', tournamentData.match_duration, 'break_duration:', tournamentData.break_duration);

        setFormData({
          name: tournamentData.name || '',
          date: isoDate,
          description: tournamentData.description || '',
          num_teams: tournamentData.num_teams?.toString() || '',
          num_courts: tournamentData.num_courts?.toString() || courtNamesArray.length.toString(),
          num_groups: tournamentData.num_groups?.toString() || '4',
          tournament_type: tournamentData.tournament_type || 'ko',
          group_assignment_mode: tournamentData.group_assignment_mode || 'auto',
          teams_advance_from_group: tournamentData.teams_advance_from_group?.toString() || '2',
          is_active: tournamentData.is_active || 0,
          max_rounds: tournamentData.max_rounds?.toString() || '5',
          ko_consolation_round: tournamentData.ko_consolation_round === 1,
          best_of_three: tournamentData.best_of_three === 1,
          show_in_presentation: tournamentData.show_in_presentation !== 0,
          draw_generated: tournamentData.draw_generated || 0,
          start_time: tournamentData.start_time || '',
          match_duration: tournamentData.match_duration?.toString() || '20',
          break_duration: tournamentData.break_duration?.toString() || '5',
          landesliga_categories: tournamentData.landesliga_categories || 'Herren 1,Herren 2,Damen,Mixed,Jugend',
        });

        if (tournamentData.tournament_type === 'landesliga') {
          const cats = (tournamentData.landesliga_categories || 'Herren 1,Herren 2,Damen,Mixed,Jugend')
            .split(',').map(c => c.trim()).filter(c => c.length > 0);
          setNewClubPairings(cats.map(category => ({
            category, active: true, players: ['', '']
          })));
        }

        console.log('Form data after loading - start_time:', tournamentData.start_time || '', 'match_duration:', tournamentData.match_duration?.toString() || '20', 'break_duration:', tournamentData.break_duration?.toString() || '5');

        setCourtNames(courtNamesArray);
        setTeams(teamsRes.data);
        setInitialLoad(false);
      } catch (err) {
        console.error('Fehler beim Laden des Turniers:', err);
        setError('Fehler beim Laden der Turnierdaten');
      } finally {
        setLoading(false);
      }
    };

    if (selectedTournamentId) {
      fetchTournamentData();
    }
  }, [selectedTournamentId]);

  // Update court names when num_courts changes
  useEffect(() => {
    if (initialLoad) return;

    const numCourts = parseInt(formData.num_courts) || 0;
    if (numCourts > 0) {
      const newCourtNames = Array.from({ length: numCourts }, (_, i) =>
        courtNames[i] || `Court ${i + 1}`
      );
      setCourtNames(newCourtNames);
    } else {
      setCourtNames([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.num_courts, initialLoad]);

  // Update teams when num_teams changes
  // (Landesliga verwaltet seine Team-Datensätze über die Vereinsverwaltung weiter unten,
  // nicht über die generische Anzahl-Teams-Logik)
  useEffect(() => {
    if (initialLoad || formData.tournament_type === 'landesliga') return;

    const numTeams = parseInt(formData.num_teams) || 0;
    if (numTeams > 0) {
      const newTeams = Array.from({ length: numTeams }, (_, i) => {
        const existingTeam = teams[i] || {};

        // Für Americano: Keine automatischen Namen generieren
        const defaultName = (formData.tournament_type === 'americano' || formData.tournament_type === 'mexicano')
          ? ''
          : `Team ${i + 1}`;

        const defaultPlayer1Name = (formData.tournament_type === 'americano' || formData.tournament_type === 'mexicano')
          ? ''
          : generateRandomPlayerName();

        const defaultPlayer2Name = (formData.tournament_type === 'americano' || formData.tournament_type === 'mexicano')
          ? ''
          : generateRandomPlayerName();

        return {
          id: existingTeam.id,
          name: existingTeam.name || defaultName,
          players: [
            { id: existingTeam.players?.[0]?.id, name: existingTeam.players?.[0]?.name || defaultPlayer1Name },
            { id: existingTeam.players?.[1]?.id, name: existingTeam.players?.[1]?.name || defaultPlayer2Name }
          ],
          mexicano_points: existingTeam.mexicano_points || 0,
          group_name: existingTeam.group_name || ''  // Preserve group_name!
        };
      });
      setTeams(newTeams);
    } else {
      setTeams([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.num_teams, initialLoad]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCourtNameChange = (index, value) => {
    const updated = [...courtNames];
    updated[index] = value;
    setCourtNames(updated);
  };

  const handlePlayerChange = (teamIndex, playerIndex, value) => {
    const updated = [...teams];
    updated[teamIndex].players[playerIndex].name = value;
    setTeams(updated);
  };

  const handleGroupChange = (teamIndex, groupName) => {
    const updated = [...teams];
    updated[teamIndex].group_name = groupName;
    setTeams(updated);
  };

  // ===== Landesliga: Vereinsverwaltung =====

  const clubsByName = () => {
    const clubs = {};
    for (const team of teams) {
      const club = team.club_name || team.name;
      if (!clubs[club]) clubs[club] = [];
      clubs[club].push(team);
    }
    return clubs;
  };

  const handleClubPairingToggle = (idx) => {
    const updated = [...newClubPairings];
    updated[idx].active = !updated[idx].active;
    setNewClubPairings(updated);
  };

  const handleClubPairingPlayerChange = (idx, playerIdx, value) => {
    const updated = [...newClubPairings];
    updated[idx].players[playerIdx] = value;
    setNewClubPairings(updated);
  };

  const handleAddClub = async () => {
    setClubError('');

    const clubName = newClubName.trim();
    if (!clubName) {
      setClubError('Bitte einen Vereinsnamen eingeben');
      return;
    }

    const activePairings = newClubPairings.filter(p => p.active);
    if (activePairings.length === 0) {
      setClubError('Bitte mindestens eine Paarung aktivieren');
      return;
    }

    setClubSaving(true);
    try {
      await axios.post(`/api/tournaments/${selectedTournamentId}/landesliga/clubs`, {
        club_name: clubName,
        pairings: activePairings.map(p => ({
          category: p.category,
          players: p.players
            .map(name => name.trim())
            .filter(name => name.length > 0)
            .map(name => ({ name }))
        }))
      });

      const updatedTeamsRes = await axios.get(`/api/tournaments/${selectedTournamentId}/teams`);
      setTeams(updatedTeamsRes.data);

      // Formular zurücksetzen
      setNewClubName('');
      const cats = formData.landesliga_categories.split(',').map(c => c.trim()).filter(c => c.length > 0);
      setNewClubPairings(cats.map(category => ({ category, active: true, players: ['', ''] })));
    } catch (err) {
      setClubError(err.response?.data?.detail || 'Fehler beim Anlegen des Vereins');
    } finally {
      setClubSaving(false);
    }
  };

  const handleRemoveClub = async (clubName) => {
    if (!window.confirm(`Verein "${clubName}" wirklich mit allen Paarungen entfernen?`)) return;

    try {
      await axios.delete(`/api/tournaments/${selectedTournamentId}/landesliga/clubs/${encodeURIComponent(clubName)}`);
      const updatedTeamsRes = await axios.get(`/api/tournaments/${selectedTournamentId}/teams`);
      setTeams(updatedTeamsRes.data);
    } catch (err) {
      setClubError(err.response?.data?.detail || 'Fehler beim Entfernen des Vereins');
    }
  };

  // Helper function to get all groups with availability info
  const getAllGroups = (currentTeamIndex) => {
    if (formData.tournament_type !== 'groups' || formData.group_assignment_mode !== 'manual') {
      return [];
    }

    const numGroups = parseInt(formData.num_groups) || 4;
    const numTeams = parseInt(formData.num_teams) || 0;
    const teamsPerGroup = Math.ceil(numTeams / numGroups);
    const currentTeam = teams[currentTeamIndex];

    // Generate all group names with availability info
    const groups = [];
    for (let i = 0; i < numGroups; i++) {
      const groupName = `Gruppe ${String.fromCharCode(65 + i)}`; // 65 = 'A'

      // Count how many teams are already in this group (excluding current team)
      const teamsInGroup = teams.filter((t, idx) =>
        idx !== currentTeamIndex && t.group_name === groupName
      ).length;

      const isFull = teamsInGroup >= teamsPerGroup;
      const isCurrentGroup = currentTeam?.group_name === groupName;

      groups.push({
        name: groupName,
        isFull: isFull,
        isCurrentGroup: isCurrentGroup,
        count: teamsInGroup
      });
    }

    return groups;
  };

  const validateForm = () => {
    const errors = [];

    // Validate name
    if (!formData.name || formData.name.trim().length < 3) {
      errors.push('Turniername muss mindestens 3 Zeichen lang sein');
    }

    // Validate date
    if (!formData.date) {
      errors.push('Datum ist erforderlich');
    }

    // Validate num_teams
    const numTeams = parseInt(formData.num_teams);
    if (formData.tournament_type === 'landesliga') {
      if (!['3', '4'].includes(String(formData.num_teams))) {
        errors.push('Anzahl Vereine muss 3 oder 4 sein');
      }
    } else if (isNaN(numTeams) || numTeams < 2 || numTeams > 64) {
      errors.push('Anzahl Teams muss zwischen 2 und 64 liegen');
    }

    // Validate num_courts
    const numCourts = parseInt(formData.num_courts);
    if (formData.tournament_type === 'landesliga') {
      if (isNaN(numCourts) || numCourts < 2 || numCourts > 6) {
        errors.push('Anzahl Courts muss für die Landesliga zwischen 2 und 6 liegen');
      }
    } else if (isNaN(numCourts) || numCourts < 1 || numCourts > 20) {
      errors.push('Anzahl Courts muss zwischen 1 und 20 liegen');
    }

    // Validate num_groups for groups tournament
    if (formData.tournament_type === 'groups') {
      const numGroups = parseInt(formData.num_groups);
      if (isNaN(numGroups) || numGroups < 2 || numGroups > 8 || numGroups % 2 !== 0) {
        errors.push('Anzahl Gruppen muss eine gerade Zahl zwischen 2 und 8 sein');
      }
    }

    // Validate court names
    if (courtNames.some(name => !name || name.trim().length === 0)) {
      errors.push('Alle Court-Namen müssen ausgefüllt sein');
    }

    // Landesliga verwaltet seine Teams (Vereins-Paarungen) über eigene Endpoints,
    // die generischen Team-/Spieler-Validierungen greifen hier nicht.
    if (formData.tournament_type !== 'landesliga') {
      // Validate teams - auto-generate team names if not set
      const teamsWithAutoNames = teams.map((team, idx) => ({
        ...team,
        name: team.name || `Team ${idx + 1}`
      }));

      // Für Americano/Mexicano: Prüfe nur den ersten Spieler (team.name)
      if (formData.tournament_type === 'americano' || formData.tournament_type === 'mexicano') {
        if (teamsWithAutoNames.some(team => !team.name || team.name.trim().length === 0)) {
          errors.push('Alle Spieler-Namen müssen ausgefüllt sein');
        }
      } else {
        // Für normale Turniere: Prüfe beide Spieler
        if (teamsWithAutoNames.some(team =>
          team.players.some(player => !player.name || player.name.trim().length === 0)
        )) {
          errors.push('Alle Spieler-Namen müssen ausgefüllt sein');
        }
      }

      // Validate manual group assignment
      if (formData.tournament_type === 'groups' && formData.group_assignment_mode === 'manual') {
        if (teams.some(team => !team.group_name)) {
          errors.push('Bei manueller Gruppenzuweisung müssen alle Teams einer Gruppe zugewiesen sein');
        }
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(' | '));
      setLoading(false);
      return;
    }

    try {
      // Prepare payload for TournamentUpdate schema
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        // Don't include date in the payload for updates
        // The backend expects date to be None for updates unless explicitly changed
        num_teams: parseInt(formData.num_teams),
        num_courts: parseInt(formData.num_courts),
        num_groups: parseInt(formData.num_groups),
        court_names: courtNames.join(', '),
        tournament_type: formData.tournament_type,
        is_active: parseInt(formData.is_active),
      };

      // Add max_rounds for mexicano tournament (user input)
      if (formData.tournament_type === 'mexicano') {
        payload.max_rounds = parseInt(formData.max_rounds);
      }

      // Add max_rounds for americano tournament (automatisch berechnet, Courts berücksichtigt)
      if (formData.tournament_type === 'americano') {
        payload.max_rounds = computeAmericanoDefaultRounds(
          parseInt(formData.num_teams),
          parseInt(formData.num_courts)
        );
      }

      // Add ko_consolation_round for ko tournament
      if (formData.tournament_type === 'ko') {
        payload.ko_consolation_round = formData.ko_consolation_round ? 1 : 0;
      }

      // Add best_of_three (available for all tournament types)
      payload.best_of_three = formData.best_of_three ? 1 : 0;
      payload.show_in_presentation = formData.show_in_presentation ? 1 : 0;

      // Add time scheduling for groups, ko_placement and landesliga tournaments
      if (formData.tournament_type === 'groups' || formData.tournament_type === 'ko_placement' || formData.tournament_type === 'landesliga') {
        if (formData.start_time) {
          payload.start_time = formData.start_time;
        }
        if (formData.match_duration) {
          payload.match_duration = parseInt(formData.match_duration);
        }
        if (formData.break_duration) {
          payload.break_duration = parseInt(formData.break_duration);
        }
      }

      // For TournamentUpdate, we DON'T include date at all
      // The backend expects date to be None for updates unless explicitly changed
      // This is different from TournamentCreate which requires date
      // Don't include date in payload - let backend keep the original date

      console.log('Sending payload:', payload);

      try {
        await axios.put(`/api/tournaments/${selectedTournamentId}`, payload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (err) {
        console.error('Error updating tournament:', err);
        console.error('Error response:', err.response?.data);
        if (err.response?.data?.detail) {
          console.error('Backend error detail:', err.response.data.detail);
        }
        throw err; // Re-throw to be caught by outer catch block
      }

      // Landesliga verwaltet seine Teams (Vereins-Paarungen) bereits direkt über die
      // Vereinsverwaltung (eigene Endpoints) - kein generischer Team-Sync nötig/gewünscht.
      if (formData.tournament_type !== 'landesliga' && teams.length > 0) {
        // Update existing teams with new data while preserving team IDs
        for (let i = 0; i < teams.length; i++) {
          try {
            const team = teams[i];

            console.log(`DEBUG Team ${i + 1}: tournament_type=${formData.tournament_type}, team=`, team);

            // For mexicano/americano, only send one player (the team itself is the player)
            const teamPayload = {
              name: team.name.trim() || (formData.tournament_type === 'americano' || formData.tournament_type === 'mexicano' ? '' : `Team ${i + 1}`),
              players: (formData.tournament_type === 'mexicano' || formData.tournament_type === 'americano')
                ? [
                    {
                      id: team.players[0]?.id || null,
                      name: team.name.trim() || '',
                      user_id: team.players[0]?.user_id || null
                    }
                  ]
                : [
                    {
                      id: team.players[0]?.id || null,
                      name: team.players[0]?.name.trim() || generateRandomPlayerName()
                    },
                    {
                      id: team.players[1]?.id || null,
                      name: team.players[1]?.name.trim() || generateRandomPlayerName()
                    },
                  ],
            };

            // Add group_name for groups tournament
            if (formData.tournament_type === 'groups') {
              // Use the group_name from the team object (set by user in dropdown)
              teamPayload.group_name = team.group_name || '';
            }

            // Add mexicano_points for mexicano tournament if they exist
            if (formData.tournament_type === 'mexicano' && team.mexicano_points !== undefined) {
              teamPayload.mexicano_points = team.mexicano_points;
            }

            // If team has an ID, update existing team, otherwise create new one
            if (team.id) {
              console.log(`Updating team ${team.id} (Team ${i + 1}) with payload:`, JSON.stringify(teamPayload, null, 2));
              await axios.put(`/api/tournaments/${selectedTournamentId}/teams/${team.id}`, teamPayload, {
                headers: {
                  'Content-Type': 'application/json',
                },
              });
              console.log(`✓ Successfully updated team ${team.id}`);
            } else {
              // Create new team if no ID exists
              const response = await axios.post(`/api/tournaments/${selectedTournamentId}/teams`, teamPayload, {
                headers: {
                  'Content-Type': 'application/json',
                },
              });
              // Update the team ID in our local state to preserve it for future updates
              const updatedTeams = [...teams];
              updatedTeams[i].id = response.data.id;
              if (response.data.players && response.data.players.length >= 2) {
                updatedTeams[i].players[0].id = response.data.players[0].id;
                updatedTeams[i].players[1].id = response.data.players[1].id;
              }
              setTeams(updatedTeams);
              console.log(`Created new team with ID ${response.data.id}`);
            }
          } catch (teamErr) {
            console.error('Error updating/creating team:', teamErr.response?.data || teamErr.message);
            // Don't fail the whole operation if team update fails
          }
        }
      }

      // Reload teams data to get updated player IDs
      const updatedTeamsRes = await axios.get(`/api/tournaments/${selectedTournamentId}/teams`);
      setTeams(updatedTeamsRes.data);

      setMessage(t('edit.saveSuccess'));
      setLoading(false);

      setTimeout(() => {
        navigate(`/admin/bracket`);
      }, 1500);

    } catch (err) {
      console.error('Fehler beim Aktualisieren:', err);
      console.error('Fehlerdetails:', err.response?.data);
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail.map(error => `${error.loc.join('.')} - ${error.msg}`).join(' | ');
          console.error('Detaillierte Validierungsfehler:', errorMessages);
          setError(`Validierungsfehler: ${errorMessages}`);
        } else {
          console.error('Einfacher Fehler:', err.response.data.detail);
          setError(`Fehler: ${err.response.data.detail}`);
        }
      } else {
        setError(t('edit.saveError'));
      }
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: 700, margin: '0 auto' }}>
      <h2>{t('edit.title')}</h2>

      <div className="form-group" style={{ marginBottom: '20px' }}>
        <label>{t('edit.selectTournament')}</label>
        <select
          value={selectedTournamentId}
          onChange={(e) => setSelectedTournamentId(e.target.value)}
          required
          style={{ width: '100%', padding: '8px' }}
        >
          <option value="">{t('edit.chooseTournament')}</option>
          {tournaments.map(tournament => (
            <option key={tournament.id} value={tournament.id}>
              {formatTournamentDisplay(tournament)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p>{t('edit.loading')}</p>
      ) : selectedTournamentId ? (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('create.name')}</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder={t('create.placeholderName')}
            />
          </div>

          <div className="form-group">
            <label>{t('create.date')}</label>
            <input
              name="date"
              type="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>{(formData.tournament_type === 'mexicano' || formData.tournament_type === 'americano') ? t('create.numPlayers') : formData.tournament_type === 'landesliga' ? t('create.numClubs') : t('create.numTeams')}</label>
            {formData.tournament_type === 'landesliga' ? (
              <select
                name="num_teams"
                value={formData.num_teams}
                onChange={handleChange}
                required
                style={{
                  width: '100%', padding: '10px', borderRadius: '6px',
                  border: '1px solid rgba(206, 212, 218, 0.7)',
                  backgroundColor: 'rgba(245, 242, 239, 0.9)',
                  color: '#0F172A', fontSize: '1rem'
                }}
              >
                <option value="3">3 Vereine</option>
                <option value="4">4 Vereine</option>
              </select>
            ) : (
              <input
                name="num_teams"
                type="number"
                min="2"
                max="64"
                value={formData.num_teams}
                onChange={handleChange}
                required
                placeholder={t('create.placeholderNumTeams')}
              />
            )}
            {formData.tournament_type === 'mexicano' && (
              <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                {t('create.helpNumTeamsMexicano')}
              </small>
            )}
            {formData.tournament_type === 'americano' && (
              <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                {t('create.helpNumTeamsAmericano')}
              </small>
            )}
            {formData.tournament_type === 'landesliga' && (
              <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                {t('create.helpNumTeamsLandesliga')}
              </small>
            )}
          </div>

          <div className="form-group">
            <label>{t('create.numCourts')}</label>
            {formData.tournament_type === 'landesliga' ? (
              <select
                name="num_courts"
                value={formData.num_courts}
                onChange={handleChange}
                required
                style={{
                  width: '100%', padding: '10px', borderRadius: '6px',
                  border: '1px solid rgba(206, 212, 218, 0.7)',
                  backgroundColor: 'rgba(245, 242, 239, 0.9)',
                  color: '#0F172A', fontSize: '1rem'
                }}
              >
                {[2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>{n} Courts</option>
                ))}
              </select>
            ) : (
              <input
                name="num_courts"
                type="number"
                min="1"
                max="20"
                value={formData.num_courts}
                onChange={handleChange}
                required
                placeholder={t('create.placeholderNumCourts')}
              />
            )}
          </div>

          {courtNames.length > 0 && (
            <div className="card" style={{ padding: '16px', marginBottom: '16px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '12px' }}>{t('create.courtNames')}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {courtNames.map((name, idx) => (
                  <div key={idx} className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Court {idx + 1}</label>
                    <input
                      value={name}
                      onChange={(e) => handleCourtNameChange(idx, e.target.value)}
                      placeholder={`Court ${idx + 1}`}
                      required
                      style={{ width: '100%', padding: '6px' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>{t('edit.formatNotChangeable')}</label>
            <select
              name="tournament_type"
              value={formData.tournament_type}
              disabled={true}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid rgba(206, 212, 218, 0.7)',
                backgroundColor: 'rgba(245, 242, 239, 0.9)',
                color: '#0F172A',
                fontSize: '1rem',
                marginTop: '8px',
                opacity: 0.6,
                cursor: 'not-allowed'
              }}
              required
            >
              <option value="ko">KO-Turnier (Single Elimination)</option>
              <option value="mexicano">Mexicano</option>
              <option value="americano">Americano</option>
              <option value="groups">Gruppenphase + K.O.-Finale</option>
            </select>
            <small style={{ color: '#fb923c', fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>
              {t('edit.formatLocked')}
            </small>
          </div>

          {formData.tournament_type === 'groups' && (
            <>
              <div className="form-group">
                <label>{t('create.numGroups')}</label>
                <input
                  name="num_groups"
                  type="number"
                  min="2"
                  max="8"
                  step="2"
                  value={formData.num_groups}
                  onChange={handleChange}
                  required
                  placeholder={t('create.placeholderNumGroups')}
                />
                <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                  {t('create.helpNumGroups')}
                </small>
              </div>

              <div className="form-group">
                <label>{t('create.groupAssignment')}</label>
                <select
                  name="group_assignment_mode"
                  value={formData.group_assignment_mode}
                  disabled={true}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(206, 212, 218, 0.7)',
                    backgroundColor: 'rgba(245, 242, 239, 0.9)',
                    color: '#0F172A',
                    fontSize: '1rem',
                    marginTop: '8px',
                    opacity: 0.6,
                    cursor: 'not-allowed'
                  }}
                  required
                >
                  <option value="auto">{t('create.groupAuto')}</option>
                  <option value="manual">{t('create.groupManual')}</option>
                </select>
                <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                  {formData.group_assignment_mode === 'auto'
                    ? t('create.helpGroupAuto')
                    : t('create.helpGroupManualEdit')}
                </small>
              </div>

              <div className="form-group">
                <label>{t('create.teamsAdvance')}</label>
                <select
                  name="teams_advance_from_group"
                  value={formData.teams_advance_from_group}
                  onChange={handleChange}
                  disabled={true}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(206, 212, 218, 0.7)',
                    backgroundColor: 'rgba(245, 242, 239, 0.5)',
                    color: '#0F172A',
                    fontSize: '1rem',
                    marginTop: '8px',
                    cursor: 'not-allowed',
                    opacity: 0.6
                  }}
                  required
                >
                  <option value="2">{t('create.teamsAdvance2')}</option>
                  <option value="3">{t('create.teamsAdvance3')}</option>
                  <option value="4">{t('create.teamsAdvance4')}</option>
                  <option value="best_plus_runners">{t('create.teamsAdvanceBestRunners')}</option>
                </select>
                <small style={{ color: '#fb923c', fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>
                  {t('create.helpTeamsAdvanceLocked')}
                </small>
              </div>

            </>
          )}

          {(formData.tournament_type === 'groups' || formData.tournament_type === 'ko_placement' || formData.tournament_type === 'landesliga') && (
            <div className="form-group" style={{
              backgroundColor: 'rgba(232, 183, 58, 0.08)',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid rgba(232, 183, 58, 0.3)',
              marginTop: '20px'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '16px', color: '#0F172A' }}>{t('create.scheduleSection')}</h4>

              <div className="form-group">
                <label>{t('create.startTime')}</label>
                <input
                  name="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={handleChange}
                  placeholder={t('create.placeholderStartTime')}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(206, 212, 218, 0.7)',
                    backgroundColor: 'white',
                    color: '#0F172A',
                    fontSize: '1rem'
                  }}
                />
                <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                  {t('create.helpStartTime')}
                </small>
              </div>

              <div className="form-group">
                <label>{t('create.matchDuration')}</label>
                <input
                  name="match_duration"
                  type="number"
                  min="10"
                  max="240"
                  value={formData.match_duration}
                  onChange={handleChange}
                  placeholder={t('create.placeholderMatchDuration')}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(206, 212, 218, 0.7)',
                    backgroundColor: 'white',
                    color: '#0F172A',
                    fontSize: '1rem'
                  }}
                />
                <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                  {t('create.helpMatchDuration')}
                </small>
              </div>

              <div className="form-group">
                <label>{t('create.breakDuration')}</label>
                <input
                  name="break_duration"
                  type="number"
                  min="0"
                  max="90"
                  value={formData.break_duration}
                  onChange={handleChange}
                  placeholder={t('create.placeholderBreakDuration')}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(206, 212, 218, 0.7)',
                    backgroundColor: 'white',
                    color: '#0F172A',
                    fontSize: '1rem'
                  }}
                />
                <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                  {t('create.helpBreakDuration')}
                </small>
              </div>
            </div>
          )}

          {formData.tournament_type === 'ko' && (
            <div className="form-group" style={{
              backgroundColor: 'rgba(108, 117, 125, 0.05)',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid rgba(108, 117, 125, 0.15)',
              opacity: formData.draw_generated === 1 ? 0.6 : 1
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: formData.draw_generated === 1 ? 'not-allowed' : 'pointer',
                gap: '12px',
                marginBottom: 0
              }}>
                <input
                  name="ko_consolation_round"
                  type="checkbox"
                  checked={formData.ko_consolation_round}
                  onChange={handleChange}
                  disabled={formData.draw_generated === 1}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: formData.draw_generated === 1 ? 'not-allowed' : 'pointer'
                  }}
                />
                <span style={{ fontSize: '1rem', fontWeight: '500' }}>
                  {t('create.consolation')}
                </span>
              </label>
              <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '8px', marginLeft: '32px' }}>
                {formData.draw_generated === 1
                  ? t('edit.consolationLocked')
                  : t('create.helpConsolation')
                }
              </small>
            </div>
          )}

          {/* Best of 3 Option - NICHT für Americano/Mexicano */}
          {formData.tournament_type !== 'americano' && formData.tournament_type !== 'mexicano' && (
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: formData.draw_generated === 1 ? 'not-allowed' : 'pointer',
                gap: '12px',
                marginBottom: 0
              }}>
                <input
                  name="best_of_three"
                  type="checkbox"
                  checked={formData.best_of_three}
                  onChange={handleChange}
                  disabled={formData.draw_generated === 1}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: formData.draw_generated === 1 ? 'not-allowed' : 'pointer'
                  }}
                />
                <span style={{ fontSize: '1rem', fontWeight: '500' }}>
                  {t('create.bestOfThree')}
                </span>
              </label>
              <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '8px', marginLeft: '32px' }}>
                {formData.draw_generated === 1
                  ? t('edit.bestOfThreeLocked')
                  : t('create.helpBestOfThreeEdit')
                }
              </small>
            </div>
          )}

          {formData.tournament_type === 'mexicano' && (
            <div className="form-group">
              <label>{t('create.numRounds')}</label>
              <input
                name="max_rounds"
                type="number"
                min="1"
                max="20"
                value={formData.max_rounds}
                onChange={handleChange}
                required
                placeholder={t('create.placeholderNumRounds')}
              />
              <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                {t('create.helpNumRoundsEdit')}
              </small>
            </div>
          )}

          {formData.tournament_type === 'americano' && (
            <div className="form-group">
              <label>{t('create.numRoundsAuto')}</label>
              <input
                type="text"
                value={`${computeAmericanoDefaultRounds(parseInt(formData.num_teams) || 0, parseInt(formData.num_courts) || 1)} (${t('create.helpAmericanoRoundsAuto')})`}
                disabled
                style={{
                  backgroundColor: 'var(--input-disabled-bg, #e9ecef)',
                  color: 'var(--input-disabled-color, #6c757d)',
                  cursor: 'not-allowed'
                }}
              />
              <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                {t('create.helpAmericanoRounds', { n: computeAmericanoDefaultRounds(parseInt(formData.num_teams) || 0, parseInt(formData.num_courts) || 1) })}
              </small>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: 0 }}>
              <input
                name="show_in_presentation"
                type="checkbox"
                checked={formData.show_in_presentation}
                onChange={handleChange}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '1rem', fontWeight: '500' }}>{t('create.showInPresentation')}</span>
            </label>
            <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '8px', marginLeft: '32px' }}>
              {t('create.helpShowInPresentation')}
            </small>
          </div>

          <div className="form-group">
            <label>{t('create.description')}</label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              placeholder={t('create.placeholderDescription')}
            />
          </div>

          {teams.length > 0 && formData.tournament_type !== 'landesliga' && (
            <div className="card" style={{ padding: '16px', marginTop: '16px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '12px' }}>
                {(formData.tournament_type === 'mexicano' || formData.tournament_type === 'americano') ? t('edit.players') : t('edit.teamsAndPlayers')}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {teams.map((team, idx) => (
                  <div key={idx} className="card" style={{ padding: 12 }}>
                    {(formData.tournament_type === 'mexicano' || formData.tournament_type === 'americano') ? (
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '0.8rem' }}>{t('edit.playerN', { n: idx + 1 })}</label>
                        <UserAutocomplete
                          value={team.players[0]?.name || team.name || ''}
                          selectedUserId={team.players[0]?.user_id || null}
                          onChange={(name, userId) => {
                            const updated = [...teams];
                            // Ensure players array exists
                            if (!updated[idx].players || updated[idx].players.length === 0) {
                              updated[idx].players = [{ name: '', id: null, user_id: null }];
                            }
                            // Preserve the player ID
                            const playerId = updated[idx].players[0]?.id || null;
                            updated[idx].players[0] = {
                              id: playerId,
                              name: name,
                              user_id: userId
                            };
                            updated[idx].name = name;
                            setTeams(updated);
                          }}
                          placeholder={t('edit.placeholderPlayerN', { n: idx + 1 })}
                          required
                        />
                      </div>
                    ) : (
                      <>
                        <div style={{
                          marginBottom: '12px',
                          padding: '8px',
                          background: 'rgba(232, 183, 58, 0.1)',
                          borderRadius: '4px',
                          borderLeft: '3px solid #e8b73a'
                        }}>
                          <div style={{
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            color: '#e8b73a'
                          }}>
                            Team {idx + 1}
                          </div>
                        </div>
                        <div className="form-group" style={{ marginBottom: 8 }}>
                          <label style={{ fontSize: '0.8rem' }}>{t('edit.player1')}</label>
                          <input
                            value={team.players[0]?.name || ''}
                            onChange={(e) => {
                              handlePlayerChange(idx, 0, e.target.value);
                              // Auto-generate team name as "Team X"
                              const updated = [...teams];
                              updated[idx].name = `Team ${idx + 1}`;
                              setTeams(updated);
                            }}
                            placeholder={t('edit.placeholderPlayer1')}
                            style={{ width: 'calc(100% - 20px)' }}
                            required
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label style={{ fontSize: '0.8rem' }}>{t('edit.player2')}</label>
                          <input
                            value={team.players[1]?.name || ''}
                            onChange={(e) => {
                              handlePlayerChange(idx, 1, e.target.value);
                              // Auto-generate team name as "Team X"
                              const updated = [...teams];
                              updated[idx].name = `Team ${idx + 1}`;
                              setTeams(updated);
                            }}
                            placeholder={t('edit.placeholderPlayer2')}
                            style={{ width: 'calc(100% - 20px)' }}
                            required
                          />
                        </div>

                        {/* Manuelle Gruppenzuweisung für Gruppenphase */}
                        {formData.tournament_type === 'groups' && formData.group_assignment_mode === 'manual' && (
                          <div className="form-group" style={{ marginTop: 12, marginBottom: 0 }}>
                            <label style={{ fontSize: '0.8rem' }}>{t('edit.assignGroup')}</label>
                            <select
                              value={team.group_name || ''}
                              onChange={(e) => handleGroupChange(idx, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '6px',
                                borderRadius: '4px',
                                border: '1px solid rgba(206, 212, 218, 0.7)',
                                backgroundColor: team.group_name ? 'rgba(34, 197, 94, 0.1)' : 'rgba(251, 146, 60, 0.1)',
                                fontSize: '0.85rem'
                              }}
                              required
                            >
                              <option value="">{t('edit.chooseGroup')}</option>
                              {getAllGroups(idx).map(group => (
                                <option
                                  key={group.name}
                                  value={group.name}
                                  disabled={group.isFull && !group.isCurrentGroup}
                                  style={{
                                    color: group.isFull && !group.isCurrentGroup ? '#999' : 'inherit'
                                  }}
                                >
                                  {group.name} {group.isFull && !group.isCurrentGroup ? t('edit.groupFull') : `(${group.count}/${Math.ceil(parseInt(formData.num_teams) / parseInt(formData.num_groups))})`}
                                </option>
                              ))}
                            </select>
                            {!team.group_name && (
                              <small style={{ color: '#fb923c', fontSize: '0.7rem', display: 'block', marginTop: '4px' }}>
                                {t('edit.assignGroupWarning')}
                              </small>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {formData.tournament_type === 'landesliga' && (
            <div className="card" style={{ padding: '16px', marginTop: '16px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '12px' }}>Vereine</h4>

              {Object.keys(clubsByName()).length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  {Object.entries(clubsByName()).map(([clubName, clubTeams]) => (
                    <div key={clubName} className="card" style={{ padding: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <strong>{clubName}</strong>
                        {formData.draw_generated !== 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveClub(clubName)}
                            style={{
                              padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem',
                              border: '1px solid rgba(239, 68, 68, 0.4)',
                              backgroundColor: 'rgba(239, 68, 68, 0.08)', color: '#ef4444',
                              cursor: 'pointer'
                            }}
                          >Entfernen</button>
                        )}
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.85rem', color: '#6c757d' }}>
                        {clubTeams.map(team => (
                          <li key={team.id}>
                            {team.pairing_category || team.name}
                            {team.players && team.players.length > 0
                              ? `: ${team.players.map(p => p.name).join(' / ')}`
                              : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {formData.draw_generated === 1 ? (
                <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                  Auslosung wurde bereits generiert – Vereine können nicht mehr hinzugefügt oder entfernt werden.
                </small>
              ) : (
                <div style={{
                  backgroundColor: 'rgba(232, 183, 58, 0.08)', padding: '16px', borderRadius: '8px',
                  border: '1px solid rgba(232, 183, 58, 0.3)'
                }}>
                  <h5 style={{ marginTop: 0, marginBottom: '12px' }}>Verein hinzufügen</h5>

                  <div className="form-group">
                    <label style={{ fontSize: '0.85rem' }}>Vereinsname</label>
                    <input
                      value={newClubName}
                      onChange={(e) => setNewClubName(e.target.value)}
                      placeholder="z.B. X3 Padel Handewitt"
                      style={{ width: '100%', padding: '8px' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                    {newClubPairings.map((pairing, idx) => (
                      <div key={idx} style={{
                        padding: 10, borderRadius: 6,
                        border: '1px solid rgba(206, 212, 218, 0.5)',
                        backgroundColor: pairing.active ? 'white' : 'rgba(108, 117, 125, 0.05)'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
                          <input
                            type="checkbox"
                            checked={pairing.active}
                            onChange={() => handleClubPairingToggle(idx)}
                          />
                          {pairing.category}
                        </label>
                        {pairing.active && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <input
                              value={pairing.players[0]}
                              onChange={(e) => handleClubPairingPlayerChange(idx, 0, e.target.value)}
                              placeholder="Spieler 1"
                              style={{ flex: 1, padding: '6px', fontSize: '0.85rem' }}
                            />
                            <input
                              value={pairing.players[1]}
                              onChange={(e) => handleClubPairingPlayerChange(idx, 1, e.target.value)}
                              placeholder="Spieler 2"
                              style={{ flex: 1, padding: '6px', fontSize: '0.85rem' }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {clubError && <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: 8 }}>{clubError}</p>}

                  <button
                    type="button"
                    onClick={handleAddClub}
                    disabled={clubSaving}
                    style={{
                      marginTop: 14, padding: '10px 18px', borderRadius: '6px',
                      border: '1px solid rgba(232, 183, 58, 0.5)',
                      backgroundColor: '#e8b73a', color: '#0F172A',
                      cursor: clubSaving ? 'not-allowed' : 'pointer', fontWeight: 600
                    }}
                  >{clubSaving ? 'Speichere...' : 'Verein speichern'}</button>
                </div>
              )}
            </div>
          )}

          <button type="submit" disabled={loading}>
            {loading ? t('edit.saving') : t('edit.saveBtn')}
          </button>
        </form>
      ) : (
        <p style={{ color: '#6c757d', textAlign: 'center', padding: '20px' }}>
          {t('edit.selectHint')}
        </p>
      )}

      {message && <p style={{ color: '#22c55e', marginTop: 16 }}>{message}</p>}
      {error && <p style={{ color: '#f87171', marginTop: 16 }}>{error}</p>}
    </div>
  );
}

export default TournamentEdit;